import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { uploadEmployeeDocument } from "@/lib/fileAccess";
import { PT_STATES_FALLBACK } from "@/lib/indiaStates";
import {
  X,
  User,
  Phone,
  Briefcase,
  Wallet,
  FileText,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Upload,
  Trash2,
  ShieldCheck,
  Plus,
  Laptop,
  UserMinus,
  GraduationCap,
} from "lucide-react";
import { payrollFlagDefaults, resolvePayrollFlags } from "@/lib/payrollFlags";
import { attendanceIncludeDefault, resolveIncludeInAttendance } from "@/lib/attendanceFlags";
import { formatDisplayDate } from "@/lib/dateFormat";

const EMPTY_ADDRESS = {
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  country: "India",
  pincode: "",
};

const STEPS = [
  { key: "basic", label: "Basic Info", icon: User },
  { key: "contact", label: "Contact", icon: Phone },
  { key: "education", label: "Education", icon: GraduationCap },
  { key: "employment", label: "Employment", icon: Briefcase },
  { key: "salary", label: "Salary & Bank", icon: Wallet },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "assets", label: "Assets", icon: Laptop },
];

function normalizeContactForForm(contact) {
  const c = contact || {};
  const comm = c.communication_address || {
    address_line1: c.address_line1 || "",
    address_line2: c.address_line2 || "",
    city: c.city || "",
    state: c.state || "",
    country: c.country || "India",
    pincode: c.pincode || "",
  };
  const perm = c.permanent_address || { ...EMPTY_ADDRESS };
  return {
    personal_email: c.personal_email || "",
    official_email: c.official_email || c.email || "",
    phone: c.phone || "",
    alt_phone: c.alt_phone || "",
    communication_address: { ...EMPTY_ADDRESS, ...comm },
    permanent_address: { ...EMPTY_ADDRESS, ...perm },
    permanent_same_as_communication: !!c.permanent_same_as_communication,
    emergency_contact_name: c.emergency_contact_name || "",
    emergency_contact_phone: c.emergency_contact_phone || "",
  };
}

const EMPTY = {
  company_id: "company",
  basic: {
    code: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    gender: "",
    date_of_birth: "",
    marital_status: "",
    blood_group: "",
    photo_url: "",
  },
  contact: {
    personal_email: "",
    official_email: "",
    phone: "",
    alt_phone: "",
    communication_address: { ...EMPTY_ADDRESS },
    permanent_address: { ...EMPTY_ADDRESS },
    permanent_same_as_communication: false,
    emergency_contact_name: "",
    emergency_contact_phone: "",
  },
  education: [],
  employment: {
    department_id: "",
    designation_id: "",
    employment_type: "Full-time",
    joining_date: "",
    work_mode: "HeadOffice",
    work_location: "",
    location_id: "",
    reporting_manager: "",
    manager_id: "",
    status: "Onboarding",
    include_in_attendance: true,
  },
  salary: {
    annual_ctc: 0,
    basic: 0,
    hra: 0,
    conveyance: 0,
    medical: 0,
    special: 0,
    other_allowances: 0,
    tds_rate: 0,
    bank_name: "",
    account_number: "",
    ifsc: "",
    pan: "",
    aadhaar: "",
    uan: "",
    pf_number: "",
    include_in_payment: true,
    generate_payslip: true,
  },
  compliance: {
    pf: true,
    esic: false,
    pt: true,
    pt_state: "",
    tds: false,
    gratuity: true,
    bonus: false,
    other_deductions: [],
  },
  documents: [],
};

const DOC_TYPES = [
  "Aadhaar Card",
  "PAN Card",
  "Resume",
  "Offer Letter",
  "Appointment Letter",
  "Joining Letter",
  "Confirmation Letter",
  "Promotion Letter",
  "Compensation Revision Letter",
  "Relieving Letter",
  "Experience Letter",
  "Education Certificate",
  "Experience Certificate",
  "Bank Passbook",
  "Address Proof",
  "Photograph",
  "Other",
];

function flagsFromEmployee(editing) {
  if (editing) {
    // Always resolve via coerce (handles MySQL 0/1 and null → type defaults)
    const payroll = resolvePayrollFlags(editing);
    return {
      include_in_attendance: resolveIncludeInAttendance(editing) === true,
      include_in_payment: payroll.include_in_payment === true,
      generate_payslip: payroll.generate_payslip === true,
    };
  }
  const pay = payrollFlagDefaults("Full-time");
  return {
    include_in_attendance: attendanceIncludeDefault("Full-time") === true,
    include_in_payment: pay.include_in_payment === true,
    generate_payslip: pay.generate_payslip === true,
  };
}

function buildEmployeeForm(editing) {
  if (editing) {
    const empType = editing.employment?.employment_type || "Full-time";
    const flags = flagsFromEmployee(editing);
    return {
      company_id: editing.company_id || "company",
      basic: { ...EMPTY.basic, ...(editing.basic || {}) },
      contact: normalizeContactForForm(editing.contact),
      employment: {
        ...EMPTY.employment,
        ...(editing.employment || {}),
        employment_type: empType,
        include_in_attendance: flags.include_in_attendance,
      },
      salary: {
        ...EMPTY.salary,
        ...(editing.salary || {}),
        include_in_payment: flags.include_in_payment,
        generate_payslip: flags.generate_payslip,
      },
      compliance: { ...EMPTY.compliance, ...(editing.compliance || {}) },
      documents: editing.documents || [],
      education: editing.education || [],
    };
  }
  const flags = flagsFromEmployee(null);
  return {
    ...EMPTY,
    salary: {
      ...EMPTY.salary,
      include_in_payment: flags.include_in_payment,
      generate_payslip: flags.generate_payslip,
    },
    employment: {
      ...EMPTY.employment,
      include_in_attendance: flags.include_in_attendance,
    },
  };
}

export default function EmployeeWizard({
  open,
  onClose,
  onSaved,
  departments,
  designations,
  companies = [],
  editing,
  allEmployees = [],
}) {
  const isEdit = !!editing;
  const editingId = editing?.id || null;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => buildEmployeeForm(editing));
  // Single source of truth for Yes/No toggles — never reset by other field edits
  const [inclusionFlags, setInclusionFlags] = useState(() => flagsFromEmployee(editing));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [pendingUploads, setPendingUploads] = useState([]);
  const [codeAuto, setCodeAuto] = useState(!editing);
  const [locations, setLocations] = useState([]);
  const [ptStates, setPtStates] = useState(PT_STATES_FALLBACK);

  useEffect(() => {
    if (!open) return;
    api.get("/locations").then((r) => setLocations((r.data || []).filter((l) => l.is_active !== false))).catch(() => setLocations([]));
    api
      .get("/pt-states")
      .then((r) => {
        if (r.data?.states?.length) setPtStates(r.data.states);
      })
      .catch(() => setPtStates(PT_STATES_FALLBACK));
  }, [open]);

  // Re-load form + flags only when opening or switching employee (not on every parent re-render)
  useEffect(() => {
    if (!open) return;
    const nextFlags = flagsFromEmployee(editing);
    setForm(buildEmployeeForm(editing));
    setInclusionFlags(nextFlags);
    setCodeAuto(!editing);
    setStep(0);
    setErr("");
    setPendingUploads([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId]);

  const setInclusionFlag = (key, value) => {
    setInclusionFlags((prev) => {
      const next = { ...prev, [key]: value === true };
      if (key === "include_in_payment" && !next.include_in_payment) {
        next.generate_payslip = false;
      }
      if (key === "generate_payslip" && next.generate_payslip) {
        next.include_in_payment = true;
      }
      return next;
    });
  };

  // Auto-suggest next employee code from Organization prefixes (new employees only)
  useEffect(() => {
    if (!open || isEdit || !codeAuto) return;
    let cancelled = false;
    const empType = form.employment?.employment_type || "Full-time";
    const cid = form.company_id || "company";
    (async () => {
      try {
        const r = await api.get("/employees/next-code", {
          params: { employment_type: empType, company_id: cid },
        });
        if (!cancelled && r.data?.code) {
          setForm((f) => ({ ...f, basic: { ...f.basic, code: r.data.code } }));
        }
      } catch {
        /* keep manual entry if API unavailable */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, codeAuto, form.employment?.employment_type, form.company_id]);

  const designationsForDept = useMemo(
    () =>
      designations.filter(
        (d) => d.department_id === form.employment.department_id
      ),
    [designations, form.employment.department_id]
  );

  if (!open) return null;

  const setField = (section, key, value) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));

  const setTopLevel = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleFile = (file, cb, onError) => {
    if (!file) return;
    const maxBytes = 15 * 1024 * 1024;
    if (file.size > maxBytes) {
      onError?.("File is too large (max 15 MB). Use a smaller PDF or image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => cb(e.target.result);
    reader.onerror = () => onError?.("Could not read the selected file. Try again.");
    reader.readAsDataURL(file);
  };

  const validateStep = () => {
    setErr("");
    if (step === 0) {
      const b = form.basic;
      if (!b.code.trim()) return "Employee Code is required";
      if (!b.first_name.trim()) return "First name is required";
      if (!b.last_name.trim()) return "Last name is required";
      if (!b.gender) return "Gender is required";
      const pan = (form.salary?.pan || "").replace(/\s+/g, "").toUpperCase();
      const aadhaar = (form.salary?.aadhaar || "").replace(/\D+/g, "");
      if (!pan && !aadhaar) return "Provide PAN or Aadhaar (or both) for identity";
      if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return "Invalid PAN format (expected ABCDE1234F)";
      if (aadhaar && !/^\d{12}$/.test(aadhaar)) return "Invalid Aadhaar format (expected 12 digits)";
    }
    if (step === 1) {
      const c = form.contact;
      if (!c.official_email?.trim()) return "Official / login email is required";
      if (!/.+@.+\..+/.test(c.official_email)) return "Invalid official email";
      if (c.personal_email && !/.+@.+\..+/.test(c.personal_email)) return "Invalid personal email";
      if (!c.phone.trim()) return "Phone is required";
    }
    if (step === 3) {
      if (!form.company_id) return "Company is required";
      const e = form.employment;
      if (!e.department_id) return "Department is required";
      if (!e.designation_id) return "Designation is required";
      const mode = e.work_mode || "HeadOffice";
      if (mode === "Branch" && !e.location_id) return "Select a Branch Office";
      if (mode === "Other") {
        if (!(e.work_location || "").trim()) return "Enter work location (manual)";
        if (!(form.compliance?.pt_state || "").trim()) return "Select PT State for Other / WFH";
      }
    }
    return null;
  };

  const next = () => {
    const e = validateStep();
    if (e) {
      setErr(e);
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    const e = validateStep();
    if (e) {
      setErr(e);
      return;
    }
    setSaving(true);
    setErr("");
    try {
      // Normalize: empty optional enum/date strings → null (gender stays required — validated below)
      const clean = JSON.parse(JSON.stringify(form));
      if (clean.basic) {
        for (const k of ["date_of_birth", "marital_status", "blood_group", "photo_url"]) {
          if (clean.basic[k] === "") clean.basic[k] = null;
        }
      }
      if (clean.employment) {
        if (clean.employment.joining_date === "") clean.employment.joining_date = null;
      }
      // Documents are stored on disk via upload API — do not send base64 in JSON.
      delete clean.documents;
      if (clean.education) {
        clean.education = clean.education.filter((e) => (e.qualification || "").trim());
      }
      if (clean.contact) {
        if (!clean.contact.personal_email) clean.contact.personal_email = null;
      }
      if (clean.compliance && clean.compliance.pt_state === "") clean.compliance.pt_state = null;
      if (clean.basic && !clean.basic.gender) {
        setErr("Gender is required");
        setSaving(false);
        return;
      }
      const mode = clean.employment?.work_mode || "HeadOffice";
      if (mode === "Branch" && !clean.employment?.location_id) {
        setErr("Select a Branch Office");
        setSaving(false);
        return;
      }
      if (mode === "Other") {
        if (!(clean.employment.work_location || "").trim()) {
          setErr("Enter work location manually");
          setSaving(false);
          return;
        }
        if (!(clean.compliance?.pt_state || "").trim()) {
          setErr("Select PT State for Other / WFH");
          setSaving(false);
          return;
        }
      }
      if (mode === "HeadOffice") {
        clean.employment.location_id = null;
        if (!(clean.employment.work_location || "").trim()) {
          clean.employment.work_location = "Head Office";
        }
        if (clean.compliance) clean.compliance.pt_state = null;
      }
      if (mode === "Other") {
        clean.employment.location_id = null;
      }
      if (mode === "Branch" && clean.compliance) {
        clean.compliance.pt_state = null;
      }
      if (!clean.employment.location_id) clean.employment.location_id = null;

      // Flags state is the only source of truth for these three toggles
      if (!clean.salary) clean.salary = {};
      if (!clean.employment) clean.employment = {};
      clean.employment.include_in_attendance = inclusionFlags.include_in_attendance === true;
      clean.salary.include_in_payment = inclusionFlags.include_in_payment === true;
      clean.salary.generate_payslip =
        inclusionFlags.include_in_payment === true && inclusionFlags.generate_payslip === true;

      if (isEdit) {
        await api.put(`/employees/${editing.id}`, clean);
      } else {
        const res = await api.post("/employees", clean);
        const newId = res.data?.id;
        if (newId && pendingUploads.length) {
          for (const item of pendingUploads) {
            await uploadEmployeeDocument(newId, item.file, {
              name: item.name,
              expiryDate: item.expiry_date || undefined,
              companyId: clean.company_id,
            });
          }
        }
      }
      onSaved();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(
        typeof d === "string"
          ? d
          : Array.isArray(d)
          ? d.map((x) => x.msg).join(", ")
          : "Failed to save"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb]">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              {isEdit ? "Edit Employee" : "Add New Employee"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Step {step + 1} of {STEPS.length} · {STEPS[step].label}
            </p>
          </div>
          <button
            data-testid="wizard-close-btn"
            onClick={onClose}
            className="p-2 text-slate-400 hover:bg-slate-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const done = idx < step;
              const current = idx === step;
              return (
                <div key={s.key} className="flex items-center flex-1 last:flex-initial">
                  <button
                    data-testid={`step-indicator-${s.key}`}
                    onClick={() => idx <= step && setStep(idx)}
                    className={`flex flex-col items-center text-center min-w-[80px] ${
                      idx <= step ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                    }`}
                  >
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center transition border-2 ${
                        current
                          ? "bg-[#226DB4] border-[#226DB4] text-white"
                          : done
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "bg-white border-slate-200 text-slate-400"
                      }`}
                    >
                      {done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </div>
                    <div
                      className={`text-[11px] mt-1.5 font-medium ${
                        current ? "text-[#226DB4]" : done ? "text-emerald-600" : "text-slate-400"
                      }`}
                    >
                      {s.label}
                    </div>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-[2px] mx-2 mb-5 ${
                        done ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {err && (
            <div
              data-testid="wizard-error"
              className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm"
            >
              {err}
            </div>
          )}

          {step === 0 && (
            <StepBasic
              form={form}
              setField={setField}
              handleFile={handleFile}
              codeAuto={codeAuto}
              onCodeManual={(v) => { setCodeAuto(false); setField("basic", "code", v); }}
              onRefreshCode={() => setCodeAuto(true)}
              onEmploymentTypeChange={(v) => {
                // Type defaults only for new employees — never reset saved toggles on edit
                if (!isEdit) {
                  const pay = payrollFlagDefaults(v);
                  const nextFlags = {
                    include_in_attendance: attendanceIncludeDefault(v) === true,
                    include_in_payment: pay.include_in_payment === true,
                    generate_payslip: pay.generate_payslip === true,
                  };
                  setInclusionFlags(nextFlags);
                  setForm((f) => ({
                    ...f,
                    employment: {
                      ...f.employment,
                      employment_type: v,
                      include_in_attendance: nextFlags.include_in_attendance,
                    },
                    salary: {
                      ...f.salary,
                      include_in_payment: nextFlags.include_in_payment,
                      generate_payslip: nextFlags.generate_payslip,
                    },
                  }));
                  setCodeAuto(true);
                } else {
                  setField("employment", "employment_type", v);
                }
              }}
              isEdit={isEdit}
            />
          )}
          {step === 1 && <StepContact form={form} setField={setField} setForm={setForm} />}
          {step === 2 && <StepEducation form={form} setForm={setForm} />}
          {step === 3 && (
            <StepEmployment
              form={form}
              setField={setField}
              setTopLevel={setTopLevel}
              flags={inclusionFlags}
              setInclusionFlag={setInclusionFlag}
              companies={companies}
              departments={departments}
              designationsForDept={designationsForDept}
              locations={locations}
              ptStates={ptStates}
              allEmployees={allEmployees}
              editing={editing}
            />
          )}
          {step === 4 && (
            <StepSalary
              form={form}
              setField={setField}
              flags={inclusionFlags}
              setInclusionFlag={setInclusionFlag}
            />
          )}
          {step === 5 && <StepCompliance form={form} setField={setField} setForm={setForm} />}
          {step === 6 && (
            <StepDocuments
              form={form}
              setForm={setForm}
              employeeId={isEdit ? editing?.id : null}
              pendingUploads={pendingUploads}
              setPendingUploads={setPendingUploads}
            />
          )}
          {step === 7 && (
            <StepAssets editing={editing} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-[#f9fafb]">
          <button
            data-testid="wizard-prev-btn"
            onClick={prev}
            disabled={step === 0}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm border border-slate-200 rounded bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <div className="text-xs text-slate-500">
            {step + 1} / {STEPS.length}
          </div>
          <div className="flex items-center gap-2">
            {isEdit && (
              <button
                data-testid="wizard-submit-btn"
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-70"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                data-testid="wizard-next-btn"
                onClick={next}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            ) : !isEdit ? (
              <button
                data-testid="wizard-submit-btn"
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-70"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Employee
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Step Components ----
const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

function StepBasic({ form, setField, handleFile, codeAuto, onCodeManual, onRefreshCode, onEmploymentTypeChange, isEdit }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 flex items-center gap-5">
          <div className="h-24 w-24 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 overflow-hidden flex items-center justify-center">
            {form.basic.photo_url ? (
              <img
                src={form.basic.photo_url}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <User className="h-10 w-10 text-slate-300" />
            )}
          </div>
          <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded cursor-pointer hover:bg-slate-50 text-slate-700">
            <Upload className="h-4 w-4" /> Upload Photo
            <input
              data-testid="photo-upload-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) =>
                handleFile(e.target.files?.[0], (url) =>
                  setField("basic", "photo_url", url)
                )
              }
            />
          </label>
          {form.basic.photo_url && (
            <button
              type="button"
              onClick={() => setField("basic", "photo_url", "")}
              className="text-xs text-rose-600 hover:underline"
            >
              Remove
            </button>
          )}
        </div>

        <Field label="First Name" required>
          <input
            data-testid="basic-firstname-input"
            className={inputCls}
            value={form.basic.first_name}
            onChange={(e) => setField("basic", "first_name", e.target.value)}
          />
        </Field>
        <Field label="Middle Name">
          <input
            data-testid="basic-middlename-input"
            className={inputCls}
            value={form.basic.middle_name || ""}
            onChange={(e) => setField("basic", "middle_name", e.target.value)}
          />
        </Field>
        <Field label="Last Name" required>
          <input
            data-testid="basic-lastname-input"
            className={inputCls}
            value={form.basic.last_name}
            onChange={(e) => setField("basic", "last_name", e.target.value)}
          />
        </Field>
        <Field label="Gender" required>
          <select
            data-testid="basic-gender-select"
            className={inputCls}
            value={form.basic.gender || ""}
            onChange={(e) => setField("basic", "gender", e.target.value)}
          >
            <option value="">Select…</option>
            <option>Male</option>
            <option>Female</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Date of Birth">
          <input
            type="date"
            className={inputCls}
            value={form.basic.date_of_birth || ""}
            onChange={(e) => setField("basic", "date_of_birth", e.target.value)}
          />
        </Field>
        <Field label="Marital Status">
          <select
            className={inputCls}
            value={form.basic.marital_status || ""}
            onChange={(e) => setField("basic", "marital_status", e.target.value)}
          >
            <option value="">Select…</option>
            <option>Single</option>
            <option>Married</option>
            <option>Divorced</option>
            <option>Widowed</option>
          </select>
        </Field>
        <Field label="Blood Group">
          <select
            className={inputCls}
            value={form.basic.blood_group || ""}
            onChange={(e) => setField("basic", "blood_group", e.target.value)}
          >
            <option value="">Select…</option>
            {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40">
        <h4 className="text-sm font-semibold text-slate-700 mb-1">Personal identity (KYC)</h4>
        <p className="text-xs text-slate-500 mb-4">
          PAN or Aadhaar (or both). These identify the person — not salary details.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="PAN">
            <input
              data-testid="basic-pan-input"
              className={`${inputCls} uppercase`}
              value={form.salary.pan || ""}
              onChange={(e) => setField("salary", "pan", e.target.value.toUpperCase())}
              placeholder="ABCDE1234F"
              maxLength={10}
            />
          </Field>
          <Field label="Aadhaar">
            <input
              data-testid="basic-aadhaar-input"
              className={inputCls}
              value={form.salary.aadhaar || ""}
              onChange={(e) =>
                setField("salary", "aadhaar", e.target.value.replace(/\D/g, "").slice(0, 12))
              }
              placeholder="12 digits"
              inputMode="numeric"
            />
          </Field>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Employment Type" required>
          <select
            data-testid="basic-emp-type-select"
            className={inputCls}
            value={form.employment.employment_type}
            onChange={(e) => {
              if (onEmploymentTypeChange) onEmploymentTypeChange(e.target.value);
              else setField("employment", "employment_type", e.target.value);
            }}
          >
            <option>Full-time</option>
            <option>Part-time</option>
            <option>Contract</option>
            <option>Intern</option>
          </select>
          {!isEdit && (
            <p className="text-[11px] text-slate-500 mt-1">
              Choose type first — Employee Code auto-updates from Organization prefixes.
            </p>
          )}
        </Field>
        <Field label="Employee Code" required>
          <div className="flex gap-2">
            <input
              data-testid="basic-code-input"
              className={inputCls}
              value={form.basic.code}
              onChange={(e) => (onCodeManual ? onCodeManual(e.target.value) : setField("basic", "code", e.target.value))}
              placeholder="Auto from type"
            />
            {!isEdit && (
              <button
                type="button"
                onClick={() => onRefreshCode?.()}
                className="px-3 py-2 text-xs border border-slate-200 rounded hover:bg-slate-50 whitespace-nowrap"
                title="Fetch next code for current employment type"
                data-testid="basic-code-refresh"
              >
                Auto
              </button>
            )}
          </div>
          {!isEdit && (
            <p className="text-[11px] text-slate-500 mt-1">
              {codeAuto
                ? "Auto from Organization → Employee ID Prefixes (updates when employment type changes)."
                : "Manual override — click Auto to use the next sequence again."}
            </p>
          )}
        </Field>
      </div>
    </div>
  );
}

function AddressBlockFields({ prefix, address, onChange }) {
  const a = address || EMPTY_ADDRESS;
  const set = (key, value) => onChange({ ...a, [key]: value });
  return (
    <>
      <div className="md:col-span-2">
        <Field label="Address Line 1">
          <input className={inputCls} value={a.address_line1 || ""} onChange={(e) => set("address_line1", e.target.value)} />
        </Field>
      </div>
      <div className="md:col-span-2">
        <Field label="Address Line 2">
          <input className={inputCls} value={a.address_line2 || ""} onChange={(e) => set("address_line2", e.target.value)} />
        </Field>
      </div>
      <Field label="City">
        <input className={inputCls} value={a.city || ""} onChange={(e) => set("city", e.target.value)} />
      </Field>
      <Field label="State">
        <input className={inputCls} value={a.state || ""} onChange={(e) => set("state", e.target.value)} />
      </Field>
      <Field label="Country">
        <input className={inputCls} value={a.country || ""} onChange={(e) => set("country", e.target.value)} />
      </Field>
      <Field label="PIN Code">
        <input className={inputCls} value={a.pincode || ""} onChange={(e) => set("pincode", e.target.value)} />
      </Field>
    </>
  );
}

function StepContact({ form, setField, setForm }) {
  const setCommAddress = (addr) =>
    setForm((f) => ({
      ...f,
      contact: {
        ...f.contact,
        communication_address: addr,
        permanent_address: f.contact.permanent_same_as_communication ? addr : f.contact.permanent_address,
      },
    }));

  const setPermAddress = (addr) =>
    setForm((f) => ({
      ...f,
      contact: { ...f.contact, permanent_address: addr },
    }));

  const toggleSameAddress = (checked) =>
    setForm((f) => ({
      ...f,
      contact: {
        ...f.contact,
        permanent_same_as_communication: checked,
        permanent_address: checked
          ? { ...f.contact.communication_address }
          : f.contact.permanent_address,
      },
    }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Official / Login Email" required>
          <input
            data-testid="contact-official-email-input"
            type="email"
            className={inputCls}
            value={form.contact.official_email || ""}
            onChange={(e) => setField("contact", "official_email", e.target.value)}
          />
          <p className="text-xs text-slate-500 mt-1">Used for system login and HR notifications.</p>
        </Field>
        <Field label="Personal Email">
          <input
            data-testid="contact-personal-email-input"
            type="email"
            className={inputCls}
            value={form.contact.personal_email || ""}
            onChange={(e) => setField("contact", "personal_email", e.target.value)}
          />
        </Field>
        <Field label="Phone" required>
          <input
            data-testid="contact-phone-input"
            className={inputCls}
            value={form.contact.phone}
            onChange={(e) => setField("contact", "phone", e.target.value)}
            placeholder="+91 9XXXXXXXXX"
          />
        </Field>
        <Field label="Alternate Phone">
          <input
            className={inputCls}
            value={form.contact.alt_phone || ""}
            onChange={(e) => setField("contact", "alt_phone", e.target.value)}
          />
        </Field>
      </div>

      <div className="border border-slate-200 rounded p-4">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Communication Address</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <AddressBlockFields
            address={form.contact.communication_address}
            onChange={setCommAddress}
          />
        </div>
      </div>

      <div className="border border-slate-200 rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-700">Permanent Address</h4>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={!!form.contact.permanent_same_as_communication}
              onChange={(e) => toggleSameAddress(e.target.checked)}
            />
            Same as communication address
          </label>
        </div>
        {!form.contact.permanent_same_as_communication && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <AddressBlockFields
              address={form.contact.permanent_address}
              onChange={setPermAddress}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Emergency Contact Name">
          <input
            className={inputCls}
            value={form.contact.emergency_contact_name || ""}
            onChange={(e) => setField("contact", "emergency_contact_name", e.target.value)}
          />
        </Field>
        <Field label="Emergency Contact Phone">
          <input
            className={inputCls}
            value={form.contact.emergency_contact_phone || ""}
            onChange={(e) => setField("contact", "emergency_contact_phone", e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

function StepEducation({ form, setForm }) {
  const rows = form.education || [];

  const updateRow = (idx, key, value) => {
    setForm((f) => {
      const list = [...(f.education || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...f, education: list };
    });
  };

  const addRow = () => {
    setForm((f) => ({
      ...f,
      education: [
        ...(f.education || []),
        {
          qualification: "",
          institution: "",
          board_university: "",
          year: "",
          grade: "",
          specialization: "",
        },
      ],
    }));
  };

  const removeRow = (idx) => {
    setForm((f) => ({
      ...f,
      education: (f.education || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Add educational qualifications. Upload degree certificates separately under Documents.
      </p>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded p-6 text-center">
          No education records yet.
        </div>
      ) : (
        rows.map((row, idx) => (
          <div key={idx} className="border border-slate-200 rounded p-4 relative">
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="absolute top-3 right-3 p-1 text-slate-400 hover:text-rose-600"
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-8">
              <Field label="Qualification" required>
                <input
                  className={inputCls}
                  value={row.qualification || ""}
                  onChange={(e) => updateRow(idx, "qualification", e.target.value)}
                  placeholder="B.Tech, MBA, 12th"
                />
              </Field>
              <Field label="Institution">
                <input
                  className={inputCls}
                  value={row.institution || ""}
                  onChange={(e) => updateRow(idx, "institution", e.target.value)}
                />
              </Field>
              <Field label="Board / University">
                <input
                  className={inputCls}
                  value={row.board_university || ""}
                  onChange={(e) => updateRow(idx, "board_university", e.target.value)}
                />
              </Field>
              <Field label="Year of Passing">
                <input
                  className={inputCls}
                  value={row.year || ""}
                  onChange={(e) => updateRow(idx, "year", e.target.value)}
                  placeholder="2018"
                />
              </Field>
              <Field label="Grade / %">
                <input
                  className={inputCls}
                  value={row.grade || ""}
                  onChange={(e) => updateRow(idx, "grade", e.target.value)}
                />
              </Field>
              <Field label="Specialization">
                <input
                  className={inputCls}
                  value={row.specialization || ""}
                  onChange={(e) => updateRow(idx, "specialization", e.target.value)}
                />
              </Field>
            </div>
          </div>
        ))
      )}
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
      >
        <Plus className="h-4 w-4" /> Add Qualification
      </button>
    </div>
  );
}

function StepEmployment({ form, setField, setTopLevel, flags, setInclusionFlag, companies, departments, designationsForDept, locations = [], ptStates = [], allEmployees, editing }) {
  const candidateManagers = (allEmployees || []).filter(
    (e) => (!editing || e.id !== editing.id) && (!form.company_id || e.company_id === form.company_id),
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Company / Legal Entity" required full>
        <select
          data-testid="emp-company-select"
          className={inputCls}
          value={form.company_id || "company"}
          onChange={(e) => setTopLevel("company_id", e.target.value)}
        >
          {(companies.length ? companies : [{ id: "company", name: "Default Company" }]).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.id})
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-500 mt-1">
          Documents stored under <code>companies/{form.company_id || "company"}/employees/…</code>
        </p>
      </Field>
      <Field label="Department" required>
        <select
          data-testid="emp-department-select"
          className={inputCls}
          value={form.employment.department_id}
          onChange={(e) => {
            setField("employment", "department_id", e.target.value);
            setField("employment", "designation_id", "");
          }}
        >
          <option value="">Select department…</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} ({d.code})
            </option>
          ))}
        </select>
      </Field>
      <Field label="Designation" required>
        <select
          data-testid="emp-designation-select"
          className={inputCls}
          value={form.employment.designation_id}
          onChange={(e) => setField("employment", "designation_id", e.target.value)}
          disabled={!form.employment.department_id}
        >
          <option value="">
            {form.employment.department_id
              ? "Select designation…"
              : "Choose department first"}
          </option>
          {designationsForDept.map((d) => (
            <option key={d.id} value={d.id}>
              {d.title}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Employment Type">
        <div className="px-3 py-2 text-sm border border-slate-100 rounded bg-slate-50 text-slate-700" data-testid="emp-type-readonly">
          {form.employment.employment_type || "Full-time"}
          <span className="text-xs text-slate-400 ml-2">(set on Basic Info)</span>
        </div>
      </Field>
      <Field label="Status">
        <select
          data-testid="emp-status-select"
          className={inputCls}
          value={form.employment.status}
          onChange={(e) => setField("employment", "status", e.target.value)}
        >
          <option>Onboarding</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </Field>
      <Field label="Joining Date">
        <input
          type="date"
          className={inputCls}
          value={form.employment.joining_date || ""}
          onChange={(e) => setField("employment", "joining_date", e.target.value)}
        />
      </Field>
      <div className="md:col-span-2 border border-slate-200 rounded-lg p-4 bg-slate-50/80">
        <YesNoToggle
          label="Include in attendance"
          hint="If Yes, this person is on the daily roster and Mark All (after joining date). Turn Off to skip Mark All; HR can still mark manually."
          value={flags.include_in_attendance === true}
          onChange={(on) => setInclusionFlag("include_in_attendance", on)}
          testid="emp-include-in-attendance"
        />
      </div>
      <Field label="Work place" required full>
        <select
          data-testid="emp-work-mode"
          className={inputCls}
          value={
            ({ Office: "HeadOffice", Remote: "Other", Hybrid: "Branch" }[form.employment.work_mode] ||
              form.employment.work_mode ||
              "HeadOffice")
          }
          onChange={(e) => {
            const v = e.target.value;
            setField("employment", "work_mode", v);
            if (v === "HeadOffice") {
              setField("employment", "location_id", "");
              if (!(form.employment.work_location || "").trim() || form.employment.work_location === "Head Office") {
                setField("employment", "work_location", "Head Office");
              }
              setField("compliance", "pt_state", "");
            }
            if (v === "Branch") {
              setField("compliance", "pt_state", "");
            }
            if (v === "Other") {
              setField("employment", "location_id", "");
            }
          }}
        >
          <option value="HeadOffice">Default Head Office</option>
          <option value="Branch">Branch Office</option>
          <option value="Other">Other (manual entry)</option>
        </select>
        <p className="text-xs text-slate-500 mt-1">
          {(form.employment.work_mode === "Branch" || form.employment.work_mode === "Hybrid") &&
            "Pick the branch — PT follows that branch’s state."}
          {(form.employment.work_mode === "Other" || form.employment.work_mode === "Remote") &&
            "For WFH / another city — type the place and select PT State."}
          {(form.employment.work_mode === "HeadOffice" ||
            form.employment.work_mode === "Office" ||
            !form.employment.work_mode) &&
            "PT uses the company / legal entity state."}
        </p>
      </Field>

      {(form.employment.work_mode === "Branch" || form.employment.work_mode === "Hybrid") && (
        <Field label="Branch" required>
          <select
            data-testid="emp-location-select"
            className={inputCls}
            value={form.employment.location_id || ""}
            onChange={(e) => {
              const loc = (locations || []).find((l) => l.id === e.target.value);
              setField("employment", "location_id", e.target.value);
              if (loc) setField("employment", "work_location", loc.name || "");
            }}
          >
            <option value="">Select branch…</option>
            {(locations || []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}{l.state ? ` (${l.state})` : ""}
              </option>
            ))}
          </select>
        </Field>
      )}

      {(form.employment.work_mode === "Other" || form.employment.work_mode === "Remote") && (
        <>
          <Field label="Work location" required>
            <input
              data-testid="emp-work-location-label"
              className={inputCls}
              value={form.employment.work_location || ""}
              onChange={(e) => setField("employment", "work_location", e.target.value)}
              placeholder="e.g. WFH – Kochi, Kerala"
            />
          </Field>
          <Field label="PT State" required>
            <select
              data-testid="emp-pt-state"
              className={inputCls}
              value={form.compliance.pt_state || ""}
              onChange={(e) => setField("compliance", "pt_state", e.target.value)}
            >
              <option value="">Select…</option>
              {(ptStates.length ? ptStates : PT_STATES_FALLBACK).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}

      <Field label="Reporting Manager">
        <select
          data-testid="emp-manager-select"
          className={inputCls}
          value={form.employment.manager_id || ""}
          onChange={(e) => {
            const m = candidateManagers.find((x) => x.id === e.target.value);
            setField("employment", "manager_id", e.target.value || null);
            setField("employment", "reporting_manager", m ? [m.basic.first_name, m.basic.middle_name, m.basic.last_name].filter(Boolean).join(" ") : "");
          }}
        >
          <option value="">— None —</option>
          {candidateManagers.map((m) => (
            <option key={m.id} value={m.id}>
              {[m.basic.first_name, m.basic.middle_name, m.basic.last_name].filter(Boolean).join(" ")} ({m.basic.code})
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

function YesNoToggle({ label, hint, value, onChange, testid }) {
  const on = Boolean(value);
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-800">{label}</span>
        {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
      </span>
      <div
        className="inline-flex shrink-0 rounded-md border border-slate-200 bg-white p-0.5"
        role="group"
        aria-label={label}
        data-testid={testid}
      >
        <button
          type="button"
          data-testid={`${testid}-yes`}
          onClick={() => onChange(true)}
          className={`px-3 py-1 text-xs font-semibold rounded transition ${
            on ? "bg-[#226DB4] text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Yes
        </button>
        <button
          type="button"
          data-testid={`${testid}-no`}
          onClick={() => onChange(false)}
          className={`px-3 py-1 text-xs font-semibold rounded transition ${
            !on ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          No
        </button>
      </div>
    </div>
  );
}

function StepSalary({ form, setField, flags, setInclusionFlag }) {
  const includePay = flags.include_in_payment === true;
  const genSlip = includePay && flags.generate_payslip === true;

  let hint;
  if (includePay && genSlip) {
    hint = "Included in Generate Payroll and an employee salary slip (ESS/PDF) is issued.";
  } else if (includePay && !genSlip) {
    hint =
      "Included in Generate Payroll (salary register / bank) — no employee salary slip (ESS/PDF). Typical for Intern stipend.";
  } else {
    hint = "Skipped in Generate Payroll (no payment, no slip).";
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="md:col-span-2 border border-slate-200 rounded-lg p-4 bg-slate-50/80">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
          Payroll inclusion
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Click <span className="font-semibold text-slate-700">Save Changes</span> after setting
          Yes/No (available on this step when editing). Closing the wizard without Save does not
          update payroll.
        </p>
          <YesNoToggle
            label="Include in payment"
            hint="Include in Generate Payroll (register / bank payout)"
            value={includePay}
            onChange={(on) => setInclusionFlag("include_in_payment", on)}
            testid="salary-include-in-payment"
          />
          <div className="border-t border-slate-200 mt-2 pt-2">
            <YesNoToggle
              label="Generate payslip"
              hint="Issue employee salary slip (ESS + PDF). Off = payment only, no slip."
              value={genSlip}
              onChange={(on) => setInclusionFlag("generate_payslip", on)}
              testid="salary-generate-payslip"
            />
          </div>
        <p className="text-xs text-[#226DB4] mt-2" data-testid="salary-payroll-hint">
          {hint}
        </p>
      </div>
      <Field label="Annual CTC (₹)">
        <input
          type="number"
          className={inputCls}
          value={form.salary.annual_ctc || 0}
          onChange={(e) => setField("salary", "annual_ctc", parseFloat(e.target.value) || 0)}
        />
      </Field>
      <Field label="TDS Rate (%)">
        <input
          type="number"
          step="0.01"
          className={inputCls}
          value={form.salary.tds_rate || 0}
          onChange={(e) => setField("salary", "tds_rate", parseFloat(e.target.value) || 0)}
        />
      </Field>
      <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-1">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
          Salary Components (Annual Amounts)
        </div>
        <p className="text-xs text-slate-500">
          Leave a field at 0 to auto-derive from CTC (Basic 50% of CTC, HRA 40% of Basic, rest →
          Special).
        </p>
      </div>
      <Field label="Basic (Annual)">
        <input
          type="number"
          className={inputCls}
          value={form.salary.basic || 0}
          onChange={(e) => setField("salary", "basic", parseFloat(e.target.value) || 0)}
        />
      </Field>
      <Field label="House Rent Allowance (Annual)">
        <input
          type="number"
          className={inputCls}
          value={form.salary.hra || 0}
          onChange={(e) => setField("salary", "hra", parseFloat(e.target.value) || 0)}
        />
      </Field>
      <Field label="Conveyance Allowance (Annual)">
        <input
          type="number"
          className={inputCls}
          value={form.salary.conveyance || 0}
          onChange={(e) => setField("salary", "conveyance", parseFloat(e.target.value) || 0)}
        />
      </Field>
      <Field label="Medical Allowance (Annual)">
        <input
          type="number"
          className={inputCls}
          value={form.salary.medical || 0}
          onChange={(e) => setField("salary", "medical", parseFloat(e.target.value) || 0)}
        />
      </Field>
      <Field label="Special Allowance (Annual)">
        <input
          type="number"
          className={inputCls}
          value={form.salary.special || 0}
          onChange={(e) => setField("salary", "special", parseFloat(e.target.value) || 0)}
        />
      </Field>
      <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-1">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Bank & Statutory Details
        </div>
      </div>
      <Field label="Bank Name">
        <input
          className={inputCls}
          value={form.salary.bank_name || ""}
          onChange={(e) => setField("salary", "bank_name", e.target.value)}
        />
      </Field>
      <Field label="Account Number">
        <input
          className={inputCls}
          value={form.salary.account_number || ""}
          onChange={(e) => setField("salary", "account_number", e.target.value)}
        />
      </Field>
      <Field label="IFSC">
        <input
          className={inputCls}
          value={form.salary.ifsc || ""}
          onChange={(e) => setField("salary", "ifsc", e.target.value)}
        />
      </Field>
      <Field label="UAN">
        <input
          className={inputCls}
          value={form.salary.uan || ""}
          onChange={(e) => setField("salary", "uan", e.target.value)}
        />
      </Field>
      <Field label="PF Number">
        <input
          className={inputCls}
          value={form.salary.pf_number || ""}
          onChange={(e) => setField("salary", "pf_number", e.target.value)}
        />
      </Field>
      <p className="md:col-span-2 text-xs text-slate-500">
        PAN and Aadhaar are under <span className="font-medium">Basic Info → Personal identity</span>.
      </p>
    </div>
  );
}

function StepCompliance({ form, setField, setForm }) {
  const toggle = (k) =>
    setField("compliance", k, !form.compliance[k]);

  const addOther = () => {
    setForm((f) => ({
      ...f,
      compliance: {
        ...f.compliance,
        other_deductions: [
          ...(f.compliance.other_deductions || []),
          { name: "", amount: 0 },
        ],
      },
    }));
  };

  const updateOther = (idx, key, value) => {
    setForm((f) => {
      const list = [...(f.compliance.other_deductions || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...f, compliance: { ...f.compliance, other_deductions: list } };
    });
  };

  const removeOther = (idx) => {
    setForm((f) => ({
      ...f,
      compliance: {
        ...f.compliance,
        other_deductions: f.compliance.other_deductions.filter((_, i) => i !== idx),
      },
    }));
  };

  const flags = [
    { k: "pf", label: "Provident Fund (PF / EPF)", note: "12% of Basic, capped at ₹1800" },
    { k: "esic", label: "ESIC", note: "0.75% if gross ≤ ₹21,000" },
    { k: "pt", label: "Professional Tax (PT)", note: "State slab schedule (location / company state)" },
    { k: "tds", label: "TDS", note: "10% above ₹50k monthly threshold (simplified)" },
    { k: "gratuity", label: "Gratuity Eligible", note: "Tracked, paid at exit" },
    { k: "bonus", label: "Statutory Bonus", note: "1/12 of Basic added to gross" },
  ];

  return (
    <div className="space-y-5">
      <div className="text-sm text-slate-600">
        Toggle which statutory deductions & benefits apply to this employee. These
        flags drive automatic payroll computation.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {flags.map((f) => (
          <button
            type="button"
            key={f.k}
            data-testid={`compliance-${f.k}`}
            onClick={() => toggle(f.k)}
            className={`text-left border rounded p-3.5 transition flex items-start justify-between gap-3 ${
              form.compliance[f.k]
                ? "border-emerald-300 bg-emerald-50/60"
                : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
          >
            <div>
              <div className="font-semibold text-slate-800 text-sm">{f.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{f.note}</div>
            </div>
            <span
              className={`mt-0.5 relative inline-flex h-5 w-9 items-center rounded-full transition flex-shrink-0 ${
                form.compliance[f.k] ? "bg-emerald-500" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition ${
                  form.compliance[f.k] ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </span>
          </button>
        ))}
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">Other Deductions</div>
            <div className="text-xs text-slate-500">
              Recurring monthly deductions (e.g. loan EMI, advance recovery)
            </div>
          </div>
          <button
            type="button"
            onClick={addOther}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50"
            data-testid="add-other-deduction-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>

        {(form.compliance.other_deductions || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No additional deductions</div>
        ) : (
          <div className="space-y-2">
            {form.compliance.other_deductions.map((od, i) => (
              <div key={od.id || `od-${i}`} className="flex items-center gap-2">
                <input
                  className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm"
                  placeholder="Deduction name (e.g. Loan EMI)"
                  value={od.name}
                  onChange={(e) => updateOther(i, "name", e.target.value)}
                />
                <input
                  type="number"
                  className="w-40 border border-slate-200 rounded px-3 py-2 text-sm"
                  placeholder="Amount"
                  value={od.amount}
                  onChange={(e) => updateOther(i, "amount", parseFloat(e.target.value) || 0)}
                />
                <button
                  type="button"
                  onClick={() => removeOther(i)}
                  className="p-2 text-rose-500 hover:bg-rose-50 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StepDocuments({ form, setForm, employeeId, pendingUploads, setPendingUploads }) {
  const [selectedType, setSelectedType] = useState(DOC_TYPES[0]);
  const [fileErr, setFileErr] = useState("");
  const [reading, setReading] = useState(false);

  const onPick = async (file) => {
    if (!file) return;
    setFileErr("");
    const maxBytes = 15 * 1024 * 1024;
    if (file.size > maxBytes) {
      setFileErr("File is too large (max 15 MB). Use a smaller PDF or image.");
      return;
    }
    setReading(true);
    try {
      if (employeeId) {
        const created = await uploadEmployeeDocument(employeeId, file, {
          name: selectedType,
          companyId: form.company_id,
        });
        setForm((f) => ({
          ...f,
          documents: [...(f.documents || []), created],
        }));
      } else {
        setPendingUploads((list) => [
          ...list,
          {
            file,
            name: selectedType,
            file_name: file.name,
            _pending: true,
          },
        ]);
      }
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setFileErr(typeof d === "string" ? d : "Upload failed");
    } finally {
      setReading(false);
    }
  };

  const removeDoc = async (doc, idx, isPending) => {
    if (isPending) {
      setPendingUploads((list) => list.filter((_, i) => i !== idx));
      return;
    }
    if (employeeId && doc.id) {
      try {
        await api.delete(`/employees/${employeeId}/documents/${doc.id}`);
      } catch (ex) {
        setFileErr(ex?.response?.data?.detail || "Could not delete document");
        return;
      }
    }
    setForm((f) => ({
      ...f,
      documents: (f.documents || []).filter((d) => d.id !== doc.id),
    }));
  };

  const totalCount = (form.documents?.length || 0) + (pendingUploads?.length || 0);

  return (
    <div className="space-y-5">
      <div className="bg-slate-50 border border-slate-200 rounded p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
            Document Type
          </label>
          <select
            data-testid="doc-type-select"
            className={inputCls}
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {DOC_TYPES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded cursor-pointer">
          <Upload className="h-4 w-4" /> Upload File
          <input
            data-testid="doc-file-input"
            type="file"
            className="hidden"
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        {reading && <span className="text-xs text-slate-500">Reading file…</span>}
        {fileErr && <p className="text-xs text-rose-600 w-full">{fileErr}</p>}
      </div>

      <div className="border border-slate-200 rounded">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
          Uploaded Documents ({totalCount})
        </div>
        {totalCount === 0 ? (
          <div className="px-4 py-8 text-center text-slate-400 text-sm">
            No documents uploaded yet
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(form.documents || []).map((d, i) => (
              <li
                key={d.id || `saved-${i}`}
                data-testid={`doc-item-${i}`}
                className="flex items-center justify-between px-4 py-3 text-sm"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-[#226DB4]" />
                  <div>
                    <div className="font-medium text-slate-700">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.file_name}</div>
                  </div>
                </div>
                <button
                  onClick={() => removeDoc(d, i, false)}
                  className="text-xs text-rose-600 hover:underline"
                >
                  Remove
                </button>
              </li>
            ))}
            {(pendingUploads || []).map((d, i) => (
              <li
                key={`pending-${i}`}
                className="flex items-center justify-between px-4 py-3 text-sm bg-amber-50/50"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-amber-600" />
                  <div>
                    <div className="font-medium text-slate-700">{d.name}</div>
                    <div className="text-xs text-slate-500">
                      {d.file_name} · uploads on Save
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeDoc(d, i, true)}
                  className="text-xs text-rose-600 hover:underline"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Tip: Aadhaar, PAN, Resume and Offer Letter are recommended for HR records.
      </p>
    </div>
  );
}


function StepAssets({ editing }) {
  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const empId = editing?.id;

  const refresh = async () => {
    if (!empId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const [mine, avail] = await Promise.all([
        api.get(`/employees/${empId}/assets`),
        api.get(`/assets?status_filter=Available`),
      ]);
      setAssigned(mine.data || []);
      setAvailable(avail.data || []);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [empId]);

  if (!empId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded p-6 text-sm text-amber-800">
        <div className="font-semibold mb-1">Save the employee first to manage assets</div>
        <div>
          Once you click <span className="font-semibold">Create Employee</span>, re-open the
          employee record and visit this step to assign laptops, ID cards, SIMs and other
          company assets.
        </div>
      </div>
    );
  }

  const assignNow = async () => {
    if (!selected) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(`/assets/${selected}/assign`, {
        employee_id: empId,
        assigned_date: new Date().toISOString().slice(0, 10),
      });
      setSelected("");
      await refresh();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to assign");
    } finally {
      setBusy(false);
    }
  };

  const unassign = async (a) => {
    if (!window.confirm(`Unassign "${a.name}"?`)) return;
    setBusy(true);
    try {
      await api.post(`/assets/${a.id}/unassign`);
      await refresh();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to unassign");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-slate-50 border border-slate-200 rounded p-4">
        <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
          Assign an Available Asset
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="wizard-asset-select"
            className={`${inputCls} flex-1 min-w-[260px]`}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={busy || loading}
          >
            <option value="">— Select an available asset —</option>
            {available.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.asset_type}{a.serial_number ? ` · ${a.serial_number}` : ""})
              </option>
            ))}
          </select>
          <button
            data-testid="wizard-asset-assign-btn"
            type="button"
            disabled={!selected || busy}
            onClick={assignNow}
            className="inline-flex items-center gap-2 bg-[#089949] hover:bg-[#067A3A] text-white px-4 py-2 text-sm rounded font-medium disabled:opacity-60"
          >
            <Plus className="h-4 w-4" /> Assign
          </button>
        </div>
        {available.length === 0 && !loading && (
          <p className="mt-2 text-xs text-slate-500">
            No available assets right now. Add one from the{" "}
            <span className="font-semibold">Asset Management</span> page.
          </p>
        )}
        {err && <div className="mt-2 text-sm text-rose-600">{err}</div>}
      </div>

      <div className="border border-slate-200 rounded">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
          Assigned Assets ({assigned.length})
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : assigned.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-400 text-sm">
            No assets assigned yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="wizard-assigned-list">
            {assigned.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between px-4 py-3 text-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center text-slate-500">
                    <Laptop className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-700">{a.name}</div>
                    <div className="text-xs text-slate-500">
                      {a.asset_type}
                      {a.serial_number && ` · ${a.serial_number}`}
                      {a.assigned_date && ` · since ${formatDisplayDate(a.assigned_date)}`}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => unassign(a)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-amber-700 border border-amber-200 hover:bg-amber-50 rounded"
                  data-testid={`wizard-unassign-${a.id}`}
                >
                  <UserMinus className="h-3.5 w-3.5" /> Unassign
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Tip: assets are managed centrally on the{" "}
        <span className="font-semibold">Asset Management</span> page. This step lets you
        quickly attach existing assets to this employee.
      </p>
    </div>
  );
}
